import workHistoryAiApi from '@/api/customer/v2/work-history-ai'

/**
 * =========================================
 * Work History AI Generation Store
 * =========================================
 *
 * Handles AI generation of work history text.
 *
 * =========================================
 */

export default {
  namespaced: true,
  state() {
    return {
      generatedText: '',
      remainingAttempts: 10,
      loading: false,
      error: null,
      errorType: null
    }
  },
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_GENERATED_TEXT(state, text) {
      state.generatedText = text
    },
    SET_REMAINING_ATTEMPTS(state, attempts) {
      state.remainingAttempts = attempts
    },
    SET_ERROR(state, { message, type }) {
      state.error = message
      state.errorType = type
    },
    CLEAR_ERROR(state) {
      state.error = null
      state.errorType = null
    },
    RESET(state) {
      state.generatedText = ''
      state.loading = false
      state.error = null
      state.errorType = null
    }
  },
  actions: {
    /**
     * Request AI generation of work history text
     *
     * @param {Object} context
     * @param {Object} payload
     * @param {string} payload.job_position
     * @param {string} payload.role
     * @param {string} payload.brief_description
     * @param {string} payload.language - 'ja' or 'en'
     * @param {number} payload.work_history_id
     */
    async generate({ commit }, payload) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await workHistoryAiApi.generate(payload)
        const data = response.data

        commit('SET_GENERATED_TEXT', data.generated_text)
        commit('SET_REMAINING_ATTEMPTS', data.remaining_attempts)
      } catch (error) {
        const status = error?.response?.status
        const serverMessage = error?.response?.data?.message

        let errorType = 'UNKNOWN'
        let message = serverMessage || 'An unexpected error occurred'

        if (!error?.response) {
          errorType = 'NETWORK_ERROR'
          message = 'Network error. Please check your connection.'
        } else if (status === 429) {
          errorType = 'RATE_LIMITED'
          message = serverMessage || 'Generation limit reached.'
        } else if (status === 503) {
          errorType = 'SERVICE_UNAVAILABLE'
          message = serverMessage || 'AI generation unavailable. Please try again later.'
        } else if (status === 504) {
          errorType = 'TIMEOUT'
          message = serverMessage || 'Request timed out. Please try again.'
        } else if (status === 422) {
          errorType = 'VALIDATION_ERROR'
          message = serverMessage || 'Invalid input data.'
        }

        commit('SET_ERROR', { message, type: errorType })
      }

      commit('SET_LOADING', false)
    },

    /**
     * Reset the store state
     */
    reset({ commit }) {
      commit('RESET')
    },

    /**
     * Clear error state
     */
    clearError({ commit }) {
      commit('CLEAR_ERROR')
    }
  }
}
